/**
 * How the console names a knowledge conversation on the rail and in the thread.
 *
 * The product's history list uses ``id`` (and sometimes ``conversation_id``). The
 * older Cortex trail list used ``trail_id``. Selection and reopen must use one
 * stable id per row; comparing ``undefined === undefined`` marks every row active.
 */
import type {
  ConsoleAskResponse,
  ConsoleCitation,
  ConsoleHistoryResponse,
  ConsoleRefusal,
  ConsoleSource,
  ConsoleTrailDetail,
  ConsoleTrailSummary,
  ConsoleTurn,
} from "./types";

export type ConversationNamed = {
  id?: string | null;
  conversation_id?: string | null;
  trail_id?: string | null;
};

export function conversationListId(row: ConversationNamed | null | undefined): string {
  if (!row) {
    return "";
  }
  return String(row.id || row.conversation_id || row.trail_id || "").trim();
}

export function conversationTitle(row: ConsoleTrailSummary): string {
  const title = (
    row.title ||
    row.first_prompt ||
    row.last_user_message ||
    ""
  ).trim();
  return title || "Untitled";
}

export function isAskAnswer(
  body: ConsoleAskResponse | ConsoleRefusal | null | undefined,
): body is ConsoleAskResponse {
  if (!body) {
    return false;
  }
  const candidate = body as ConsoleAskResponse;
  if (candidate.response_type !== "console_knowledge_answer") {
    return false;
  }
  return (
    Array.isArray(candidate.sources) ||
    Array.isArray(candidate.citations) ||
    typeof candidate.answer === "string"
  );
}

export function askConversationId(body: ConsoleAskResponse): string {
  return String(body.conversation_id || body.product_conversation_id || "").trim();
}

export function citationsFromSources(sources: ConsoleSource[] | undefined): ConsoleCitation[] {
  return (sources || []).map((source, index) => ({
    id: String(index + 1),
    title: source.path || "Source",
    filepath: source.path || null,
    content: "",
    url: source.provenance_uri || null,
    metadata: null,
    chunk_id: null,
    path: source.path,
    marking: source.marking,
    last_reviewed: source.last_reviewed,
  }));
}

function citationsFromUnknown(value: unknown): ConsoleCitation[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item) => item && typeof item === "object") as ConsoleCitation[];
}

function citationsFromToolContent(content: unknown): ConsoleCitation[] {
  if (content && typeof content === "object" && !Array.isArray(content)) {
    return citationsFromUnknown((content as { citations?: unknown }).citations);
  }
  if (typeof content !== "string" || !content.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(content) as { citations?: unknown };
    return citationsFromUnknown(parsed?.citations);
  } catch {
    return [];
  }
}

function emptyTurn(seq: number, prompt: string, answer: string | null): ConsoleTurn {
  return {
    seq,
    prompt,
    answer,
    outcome_type: "success",
    sources: [],
    citations: [],
    ceiling_applied: null,
    created_at: null,
  };
}

/**
 * The product history read is ``{ conversation_id, messages }``. The older Cortex
 * trail read is ``{ trail_id, turns }``. The thread renders turns, so this maps
 * either shape into that one view without rewriting answer text.
 */
export function asTrailDetail(raw: unknown): ConsoleTrailDetail | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const conv = raw as Record<string, unknown> & ConsoleTrailDetail;
  if (Array.isArray(conv.turns)) {
    const id = conversationListId(conv);
    return {
      ...conv,
      trail_id: conv.trail_id || id,
      turns: conv.turns,
    };
  }
  const messages = Array.isArray(conv.messages) ? conv.messages : null;
  if (!messages) {
    return null;
  }
  const turns: ConsoleTurn[] = [];
  let seq = 0;
  let pendingPrompt: string | null = null;
  let pendingCitations: ConsoleCitation[] = [];
  for (const item of messages) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const msg = item as {
      role?: string;
      content?: unknown;
      createdAt?: string | null;
      citations?: unknown;
      sources?: ConsoleSource[];
    };
    const role = String(msg.role || "").trim().toLowerCase();
    if (role === "tool") {
      const fromTool = citationsFromToolContent(msg.content);
      if (fromTool.length) {
        pendingCitations = fromTool;
      }
      continue;
    }
    if (role === "user") {
      if (pendingPrompt) {
        turns.push(emptyTurn(++seq, pendingPrompt, null));
      }
      pendingPrompt = String(msg.content ?? "");
      continue;
    }
    if (role === "assistant") {
      const citations =
        citationsFromUnknown(msg.citations).length > 0
          ? citationsFromUnknown(msg.citations)
          : pendingCitations;
      turns.push({
        seq: ++seq,
        prompt: pendingPrompt || "",
        answer: String(msg.content ?? ""),
        outcome_type: "success",
        sources: msg.sources || [],
        citations,
        ceiling_applied: null,
        created_at: msg.createdAt || null,
      });
      pendingPrompt = null;
      pendingCitations = [];
    }
  }
  if (pendingPrompt) {
    turns.push(emptyTurn(++seq, pendingPrompt, null));
  }
  const id = conversationListId({
    id: typeof conv.id === "string" ? conv.id : null,
    conversation_id:
      typeof conv.conversation_id === "string" ? conv.conversation_id : null,
    trail_id: typeof conv.trail_id === "string" ? conv.trail_id : null,
  });
  return {
    trail_id: id,
    started_at: null,
    last_turn_at: null,
    turn_count: turns.length,
    classification: null,
    turns,
  };
}

export function historyConversation(
  body: ConsoleHistoryResponse | ConsoleRefusal,
): ConsoleTrailDetail | null {
  const candidate = body as ConsoleHistoryResponse;
  if (candidate.conversation) {
    return asTrailDetail(candidate.conversation);
  }
  if (Array.isArray(candidate.messages)) {
    return asTrailDetail(candidate);
  }
  return null;
}
